import {
	buildMonthBreakdown,
	flattenPropertyPayments,
	getSavedMonthEntry,
	toFiniteNumber,
} from "./financialStatus";

export const OVERVIEW_STATUSES = {
	empty: { key: "empty", colorScheme: "blue" },
	transferred: { key: "transferred", colorScheme: "green" },
	received: { key: "received", colorScheme: "yellow" },
	unpaid: { key: "unpaid", colorScheme: "red" },
};

export const FINALIZATION_STATUSES = [
	"custodie",
	"transferat",
	"ownerAccount",
	"predareCash",
	"transferCustodie",
	"predareCashCustodie",
];

function ownerIdentity(owner) {
	return owner?.id || owner?.email || "unknown-owner";
}

export function hasActiveTenant(property) {
	return (property?.tenants || []).some((tenant) => !tenant?.former);
}

export function getOverviewPropertyStatus({ property, rentAmount }) {
	if (!hasActiveTenant(property)) return OVERVIEW_STATUSES.empty;
	if (toFiniteNumber(rentAmount) === 0) return OVERVIEW_STATUSES.unpaid;
	return OVERVIEW_STATUSES.received;
}

export function groupPropertiesByOwner(properties = []) {
	const groups = new Map();

	(properties || []).forEach((property) => {
		const owner = property?.owner;
		if (!owner) return;
		const identity = ownerIdentity(owner);
		if (!groups.has(identity)) {
			groups.set(identity, { owner, properties: [] });
		}
		groups.get(identity).properties.push(property);
	});

	return [...groups.values()].sort((a, b) =>
		String(a.owner?.name || "").localeCompare(String(b.owner?.name || ""), "ro", { sensitivity: "base" }),
	);
}

function createStatusCounts() {
	return { empty: 0, transferred: 0, received: 0, unpaid: 0 };
}

function createCurrencyTotals() {
	return { eur: 0, ron: 0 };
}

export function buildFinancialStatusOverview({ properties = [], year, monthIndex } = {}) {
	const warnings = [];
	const statusCounts = createStatusCounts();
	const finalizedTotals = Object.fromEntries(FINALIZATION_STATUSES.map((status) => [status, createCurrencyTotals()]));

	const owners = groupPropertiesByOwner(properties).map(({ owner, properties: ownerProperties }) => {
		const ownerWarnings = [];
		const rawOwnerCurrency = String(owner?.bankDetails?.currency || "").toLowerCase();
		const ownerCurrency = ["eur", "ron"].includes(rawOwnerCurrency) ? rawOwnerCurrency : "";
		if (!ownerCurrency) {
			ownerWarnings.push({ code: "missing_owner_currency", ownerId: ownerIdentity(owner), ownerName: owner?.name });
		}

		const payments = flattenPropertyPayments(ownerProperties, ownerWarnings, { warnDates: true });
		const month = buildMonthBreakdown({
			properties: ownerProperties,
			year,
			monthIndex,
			ownerCurrency,
			payments,
		});
		ownerWarnings.push(...month.warnings);

		const savedMonth = getSavedMonthEntry(owner?.financialStatus, year, monthIndex);
		const savedCurrency = String(savedMonth?.currency || "").toLowerCase();
		if (savedMonth?.status && ["eur", "ron"].includes(savedCurrency) && finalizedTotals[savedMonth.status]) {
			finalizedTotals[savedMonth.status][savedCurrency] += toFiniteNumber(savedMonth.amount);
		}
		if (savedMonth?.amount && ["eur", "ron"].includes(savedCurrency)) {
			const live = toFiniteNumber(month.totals.net?.[savedCurrency]);
			const savedAmount = toFiniteNumber(savedMonth.amount);
			if (Math.abs(live - savedAmount) > 0.009) {
				ownerWarnings.push({
					code: "finalized_differs",
					year,
					monthIndex,
					savedAmount,
					liveAmount: live,
					currency: savedCurrency,
				});
			}
		}

		const rows = month.byProperty.map((row, index) => {
			const property = ownerProperties[index];
			const status = getOverviewPropertyStatus({
				property,
				rentAmount: row.rent.amount,
			});
			statusCounts[status.key] += 1;

			return {
				...row,
				property,
				status,
			};
		});

		const ownerStatus = savedMonth?.status
			? OVERVIEW_STATUSES.transferred
			: rows.length === 0
				? OVERVIEW_STATUSES.empty
				: rows.some((row) => row.status.key === "unpaid")
					? OVERVIEW_STATUSES.unpaid
					: rows.some((row) => row.status.key === "received")
						? OVERVIEW_STATUSES.received
						: OVERVIEW_STATUSES.empty;

		warnings.push(
			...ownerWarnings.map((warning) => ({
				...warning,
				ownerId: ownerIdentity(owner),
				ownerName: owner?.name,
				ownerRouteId: owner?.id,
			})),
		);

		return {
			owner,
			ownerId: ownerIdentity(owner),
			ownerCurrency,
			savedMonth,
			status: ownerStatus,
			rows,
			totals: month.totals,
			warnings: ownerWarnings,
		};
	});

	return {
		year,
		monthIndex,
		owners,
		statusCounts,
		finalizedTotals,
		warnings,
		propertyCount: owners.reduce((sum, owner) => sum + owner.rows.length, 0),
	};
}
